import consumer from "./consumer"

consumer.subscriptions.create("NotificationChannel", {
  connected() {
    console.log("Connected to NotificationChannel")
    this.requestNotificationPermission()
  },

  disconnected() {
    console.log("Disconnected from NotificationChannel")
  },

  received(data) {
    console.log("Received notification data:", data)

    // Update all notification badges (desktop and mobile)
    const badges = document.querySelectorAll('[data-notification-badge]')
    const count = data.unread_count

    badges.forEach(badge => {
      if (count > 0) {
        // Show badge with count
        badge.textContent = count
        badge.style.display = 'inline-flex'
      } else {
        // Hide badge
        badge.style.display = 'none'
      }
    })

    // Update approval badges
    if (data.approval_count !== undefined) {
      const approvalBadges = document.querySelectorAll('[data-approval-badge]')
      const approvalCount = data.approval_count

      approvalBadges.forEach(badge => {
        if (approvalCount > 0) {
          // Show badge with count
          badge.textContent = approvalCount
          badge.style.display = 'inline-flex'
        } else {
          // Hide badge
          badge.style.display = 'none'
        }
      })
    }

    // Handle new notifications
    if (data.new_notification && data.title) {
      // Show toast notification
      this.showToast(data.title, data.message, data.notification_type)

      // Show browser notification if permission granted
      this.showBrowserNotification(data.title, data.message, data.notification_type)

      // Play sound for important notifications
      if (this.isImportantNotification(data.notification_type)) {
        this.playNotificationSound()
      }
    }
  },

  requestNotificationPermission() {
    // Only request if browser supports notifications and permission not yet determined
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        console.log('Notification permission:', permission)
      })
    }
  },

  showBrowserNotification(title, message, type) {
    // Check if browser supports notifications and permission is granted
    if (!('Notification' in window) || Notification.permission !== 'granted') {
      return
    }

    // Don't show browser notification if page is focused (toast is enough)
    if (document.hasFocus()) {
      return
    }

    const options = {
      body: message,
      icon: '/icon.png', // You can add a proper icon path
      badge: '/badge.png', // Small icon for notification tray
      tag: type, // Prevents duplicate notifications of same type
      requireInteraction: this.isImportantNotification(type), // Keep important notifications until clicked
      vibrate: this.isImportantNotification(type) ? [200, 100, 200] : undefined
    }

    const notification = new Notification(title, options)

    // Close notification after 10 seconds if not important
    if (!this.isImportantNotification(type)) {
      setTimeout(() => notification.close(), 10000)
    }

    // Navigate to notifications page when clicked
    notification.onclick = () => {
      window.focus()
      window.location.href = '/admin/notifications'
      notification.close()
    }
  },

  isImportantNotification(type) {
    // Define which notification types are important
    const importantTypes = [
      'direct_sale_approval_needed',
      'approval_request',
      'direct_sale_approved',
      'direct_sale_rejected',
      'follow_up_overdue',
      'follow_up_now',
      'follow_up_reminder_10min',
      'pix_expiring_soon',
      'pix_expired'
    ]
    return importantTypes.includes(type)
  },

  playNotificationSound() {
    // Create a simple notification sound using Web Audio API
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)()
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)

      // Configure sound (pleasant notification tone)
      oscillator.frequency.value = 800
      oscillator.type = 'sine'
      gainNode.gain.value = 0.1

      // Play short beep
      oscillator.start(audioContext.currentTime)
      oscillator.stop(audioContext.currentTime + 0.15)

      // Second beep for important notifications
      const oscillator2 = audioContext.createOscillator()
      const gainNode2 = audioContext.createGain()
      oscillator2.connect(gainNode2)
      gainNode2.connect(audioContext.destination)
      oscillator2.frequency.value = 1000
      oscillator2.type = 'sine'
      gainNode2.gain.value = 0.1
      oscillator2.start(audioContext.currentTime + 0.2)
      oscillator2.stop(audioContext.currentTime + 0.35)
    } catch (error) {
      console.log('Could not play notification sound:', error)
    }
  },

  showToast(title, message, type) {
    // Determine color based on notification type
    const colorClass = this.getToastColor(type)

    // Create a simple toast notification
    const toast = document.createElement('div')
    toast.className = `fixed top-20 right-4 z-50 ${colorClass} text-white px-4 py-3 rounded-lg shadow-lg max-w-sm animate-fade-in-down`
    toast.innerHTML = `
      <div class="flex items-start gap-3">
        <svg class="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        <div class="flex-1">
          <p class="font-semibold text-sm">${title}</p>
          ${message ? `<p class="text-xs mt-1 opacity-90">${message}</p>` : ''}
        </div>
      </div>
    `

    document.body.appendChild(toast)

    // Remove toast after 5 seconds
    setTimeout(() => {
      toast.classList.add('animate-fade-out-up')
      setTimeout(() => toast.remove(), 300)
    }, 5000)
  },

  getToastColor(type) {
    switch(type) {
      case 'direct_sale_approved':
        return 'bg-green-500'
      case 'direct_sale_rejected':
      case 'follow_up_overdue':
      case 'pix_expired':
        return 'bg-red-500'
      case 'direct_sale_approval_needed':
      case 'approval_request':
      case 'pix_expiring_soon':
      case 'follow_up_reminder_10min':
      case 'follow_up_now':
        return 'bg-yellow-500'
      case 'payment_link_ready':
        return 'bg-orange-500'
      case 'follow_up_reminder':
      case 'follow_up_reminder_1day':
      case 'follow_up_reminder_1hour':
      case 'follow_up_reminder_30min':
        return 'bg-purple-500'
      case 'contract_ready':
        return 'bg-blue-500'
      default:
        return 'bg-blue-500'
    }
  }
});
